import {
  ENDPOINTS,
  ExecuteSharepointIngestRequestSchema,
  PlanSharepointIngestRequestSchema,
  type ExecuteSharepointIngestRequest,
  type ExecuteSharepointIngestResponse,
  type PlanSharepointIngestRequest,
  type PlanSharepointIngestResponse,
  ReopenSharepointDocumentRequestSchema,
  SkipSharepointDocumentRequestSchema,
  type ReopenSharepointDocumentRequest,
  type ReopenSharepointDocumentResponse,
  type SkipSharepointDocumentRequest,
  type SkipSharepointDocumentResponse,
} from '@clm/shared-types';

import { apiPost } from './client';

/**
 * `plan-sharepoint-ingest` (CLM-43) — the first endpoint-specific wrapper.
 *
 * client.ts deliberately shipped without wrappers because the endpoint
 * contracts had not landed; this one exists in @clm/shared-types, so the
 * wrapper is written against the real contract rather than a guess:
 *
 *  - The path comes from the endpoint registry, not a string literal, so the
 *    SPA cannot drift from the contract of record.
 *  - The request is validated through the shared schema before it leaves the
 *    browser — the schema is `.strict()`, so a scope-shaped or invented field
 *    is a thrown ZodError here, never a payload on the wire.
 *  - The response is **parsed**, not asserted. `dry_run: z.literal(true)` and
 *    every enum below it are checked at the boundary, so the surfaces render
 *    values the contract vouches for. A response that does not parse is a
 *    defect to report, not data to display.
 *
 * Rule 11 holds: this posts a pasted URL to the gateway and nothing more. The
 * crawl, the Graph client and the managed identity live server-side in
 * @clm/sharepoint, which eslint bans this app from importing even as types.
 */
const PLAN_ENDPOINT = ENDPOINTS['plan-sharepoint-ingest'];

export async function planSharepointIngest(
  request: PlanSharepointIngestRequest,
  signal?: AbortSignal,
): Promise<PlanSharepointIngestResponse> {
  const body = PlanSharepointIngestRequestSchema.parse(request);
  const raw = await apiPost<unknown>(
    PLAN_ENDPOINT.path,
    body,
    signal === undefined ? {} : { signal },
  );
  return PLAN_ENDPOINT.response.parse(raw);
}

/**
 * `execute-sharepoint-ingest` (CLM-50; wired to the planner surface by CLM-67).
 *
 * The same recipe as the plan wrapper: path from the registry, request parsed
 * through the `.strict()` shared schema so only `(drive_id, item_id, etag)`
 * triples, the folder link and the two optional plan mirrors can leave the
 * browser — a client-supplied `party_key` or `sow_number` is a ZodError here,
 * never a payload — and the 202 receipt parsed, not asserted. Rule 11 holds:
 * the crawl, the download and every write happen server-side.
 */
const EXECUTE_ENDPOINT = ENDPOINTS['execute-sharepoint-ingest'];

export async function executeSharepointIngest(
  request: ExecuteSharepointIngestRequest,
  signal?: AbortSignal,
): Promise<ExecuteSharepointIngestResponse> {
  const body = ExecuteSharepointIngestRequestSchema.parse(request);
  const raw = await apiPost<unknown>(
    EXECUTE_ENDPOINT.path,
    body,
    signal === undefined ? {} : { signal },
  );
  return EXECUTE_ENDPOINT.response.parse(raw);
}

/**
 * `skip-sharepoint-document` and `reopen-sharepoint-document` (CLM-115).
 *
 * The same recipe again: path from the registry, request through the `.strict()`
 * shared schema, response parsed rather than asserted.
 *
 * Neither sends the file name or the web URL. The server has both from its own
 * crawl, and a client-supplied name here would be a second, unverified source
 * for what the row is — the same reasoning that keeps `party_key` out of the
 * execute request.
 */
const SKIP_ENDPOINT = ENDPOINTS['skip-sharepoint-document'];

export async function skipSharepointDocument(
  request: SkipSharepointDocumentRequest,
  signal?: AbortSignal,
): Promise<SkipSharepointDocumentResponse> {
  const body = SkipSharepointDocumentRequestSchema.parse(request);
  const raw = await apiPost<unknown>(
    SKIP_ENDPOINT.path,
    body,
    signal === undefined ? {} : { signal },
  );
  return SKIP_ENDPOINT.response.parse(raw);
}

const REOPEN_ENDPOINT = ENDPOINTS['reopen-sharepoint-document'];

export async function reopenSharepointDocument(
  request: ReopenSharepointDocumentRequest,
  signal?: AbortSignal,
): Promise<ReopenSharepointDocumentResponse> {
  const body = ReopenSharepointDocumentRequestSchema.parse(request);
  const raw = await apiPost<unknown>(
    REOPEN_ENDPOINT.path,
    body,
    signal === undefined ? {} : { signal },
  );
  return REOPEN_ENDPOINT.response.parse(raw);
}
